import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import {
  DEFAULT_LOCALE,
  isSupportedLocale,
  preferredSupportedLocale,
  SUPPORTED_LOCALES,
  SUPPORTED_LOCALES_ARRAY,
  type SupportedLocale,
} from "./i18n-locales";
import { readableMissingTranslation } from "./lib/translation-fallback";

// Re-export the pure locale constants so existing imports from "@/i18n" keep
// working unchanged. The definitions live in "./i18n-locales" so the Convex
// backend can import them without pulling in `import.meta.glob` below.
export {
  DEFAULT_LOCALE,
  isSupportedLocale,
  preferredSupportedLocale,
  SUPPORTED_LOCALES,
  SUPPORTED_LOCALES_ARRAY,
  type SupportedLocale,
};

// Saved locale from localStorage (computed once at module load)
export const SAVED_LOCALE =
  typeof window !== "undefined"
    ? (() => {
        try {
          const stored = localStorage.getItem("locale");
          return stored && isSupportedLocale(stored) ? stored : null;
        } catch {
          // localStorage might be disabled (private browsing, etc.)
          return null;
        }
      })()
    : null;

// Saved locale or default locale
export const BROWSER_LOCALE =
  typeof navigator !== "undefined"
    ? preferredSupportedLocale(navigator.languages)
    : null;

export const SAVED_OR_DEFAULT_LOCALE: SupportedLocale =
  SAVED_LOCALE ?? BROWSER_LOCALE ?? DEFAULT_LOCALE;

const PATH_LOCALE =
  typeof window !== "undefined"
    ? window.location.pathname.split("/").filter(Boolean)[0]?.toLowerCase()
    : null;
const INITIAL_LOCALE: SupportedLocale =
  PATH_LOCALE && isSupportedLocale(PATH_LOCALE)
    ? PATH_LOCALE
    : SAVED_OR_DEFAULT_LOCALE;

const translationLoaders = import.meta.glob<{
  default: Record<string, string>;
}>("./locales/*/*.json");
const loadedLocales = new Set<SupportedLocale>();

async function fetchLocaleResources(locale: SupportedLocale) {
  const prefix = `./locales/${locale}/`;
  const modules = await Promise.all(
    Object.entries(translationLoaders)
      .filter(([modulePath]) => modulePath.startsWith(prefix))
      .map(async ([modulePath, load]) => ({
        modulePath,
        module: await load(),
      })),
  );
  const resources: Record<string, Record<string, string>> = {};
  for (const { modulePath, module } of modules) {
    const namespace = modulePath.match(/\/([^/]+)\.json$/)?.[1];
    if (namespace) {
      resources[namespace] = module.default;
    }
  }
  return resources;
}

async function loadLocaleResources(locale: SupportedLocale) {
  if (loadedLocales.has(locale)) return;
  const resources = await fetchLocaleResources(locale);
  for (const [namespace, translations] of Object.entries(resources)) {
    i18n.addResourceBundle(locale, namespace, translations, true, false);
  }
  loadedLocales.add(locale);
}

// Set locale in path - handles all locale routing logic
export function setLocaleInPath(
  locale: string,
  pathname: string,
  search: string = "",
  hash: string = "",
): string {
  // Use saved/default locale if provided locale is invalid
  const targetLocale = isSupportedLocale(locale)
    ? locale
    : SAVED_OR_DEFAULT_LOCALE;

  // Handle root path
  if (pathname === "/") {
    return `/${targetLocale}${search}${hash}`;
  }

  // Split pathname into segments
  const segments = pathname.split("/").filter(Boolean);
  const firstSegment = segments[0];
  const firstSegmentLowercase = firstSegment?.toLowerCase();

  // If first segment is a valid locale (case-insensitive), replace it
  if (firstSegmentLowercase && isSupportedLocale(firstSegmentLowercase)) {
    segments[0] = targetLocale;
    return `/${segments.join("/")}${search}${hash}`;
  }

  // Otherwise, prepend the locale
  return `/${targetLocale}${pathname}${search}${hash}`;
}

// Utility function to change locale and update document attributes
export async function changeLocale(lng: SupportedLocale) {
  try {
    await loadLocaleResources(lng);
    await i18n.changeLanguage(lng);
    const localeMetadata = SUPPORTED_LOCALES[lng];
    document.documentElement.lang = lng;
    document.documentElement.dir = localeMetadata.dir ?? "ltr";

    try {
      localStorage.setItem("locale", lng);
    } catch {
      // localStorage might be disabled - fail silently
    }
  } catch (error) {
    console.error("Failed to change locale:", error);
    throw error;
  }
}

const initialResources: Record<
  string,
  Record<string, Record<string, string>>
> = {};
for (const locale of new Set([DEFAULT_LOCALE, INITIAL_LOCALE])) {
  initialResources[locale] = await fetchLocaleResources(locale);
  loadedLocales.add(locale);
}

await i18n.use(initReactI18next).init({
  resources: initialResources,
  lng: INITIAL_LOCALE,
  fallbackLng: DEFAULT_LOCALE,
  supportedLngs: SUPPORTED_LOCALES_ARRAY,
  defaultNS: "common",
  parseMissingKeyHandler: readableMissingTranslation,
  interpolation: { escapeValue: false }, // React already escapes values
  react: { useSuspense: true },
});

export default i18n;
